import React from "react"
import { Outlet } from "react-router-dom"
import AuthenticatedHeader from "../components/globals/AuthenticatedHeader"
import Toaster from "../components/globals/Toaster"
import { ModalRootElement } from "../components/elements/Modal"

// tailwind css
import '../index.css'

const desktopSidebarStorageKey = "prismancy.desktopSidebarCollapsed"
const desktopSidebarPanelTransitionMs = 200

const AuthenticatedLayout: React.FC = (): React.ReactElement => {
  const [isDesktopSidebarCollapsed, setIsDesktopSidebarCollapsed] = React.useState(() => {
    if (typeof window === "undefined") return false

    try {
      return window.localStorage.getItem(desktopSidebarStorageKey) === "true"
    } catch {
      return false
    }
  })
  const [isDesktopSidebarContentHidden, setIsDesktopSidebarContentHidden] = React.useState(isDesktopSidebarCollapsed)
  const [isDesktopSidebarTransitioning, setIsDesktopSidebarTransitioning] = React.useState(false)
  const transitionTimerRef = React.useRef<number | null>(null)

  const clearTransitionTimers = React.useCallback(() => {
    if (transitionTimerRef.current !== null) {
      window.clearTimeout(transitionTimerRef.current)
      transitionTimerRef.current = null
    }
  }, [])

  React.useEffect(() => clearTransitionTimers, [clearTransitionTimers])

  const handleToggleDesktopSidebar = () => {
    if (isDesktopSidebarTransitioning) return

    const nextValue = !isDesktopSidebarCollapsed

    try {
      window.localStorage.setItem(desktopSidebarStorageKey, String(nextValue))
    } catch {
      // The sidebar still works when browser storage is unavailable.
    }

    clearTransitionTimers()

    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
      setIsDesktopSidebarContentHidden(nextValue)
      setIsDesktopSidebarCollapsed(nextValue)
      setIsDesktopSidebarTransitioning(false)
      return
    }

    setIsDesktopSidebarTransitioning(true)

    if (nextValue) {
      setIsDesktopSidebarContentHidden(true)
      setIsDesktopSidebarCollapsed(true)
      transitionTimerRef.current = window.setTimeout(() => {
        setIsDesktopSidebarTransitioning(false)
        transitionTimerRef.current = null
      }, desktopSidebarPanelTransitionMs)
      return
    }

    setIsDesktopSidebarContentHidden(false)
    setIsDesktopSidebarCollapsed(false)
    transitionTimerRef.current = window.setTimeout(() => {
      setIsDesktopSidebarTransitioning(false)
      transitionTimerRef.current = null
    }, desktopSidebarPanelTransitionMs)
  }

  return (
    <div className={`ui-authenticated-layout ui-panel-view flex h-full w-full flex-col overflow-hidden ${isDesktopSidebarCollapsed ? "ui-desktop-sidebar-collapsed" : ""} ${isDesktopSidebarContentHidden ? "ui-desktop-sidebar-content-hidden" : ""} ${isDesktopSidebarTransitioning && !isDesktopSidebarCollapsed ? "ui-desktop-sidebar-expanding" : ""}`}>
      <AuthenticatedHeader
        isDesktopSidebarCollapsed={isDesktopSidebarCollapsed}
        onToggleDesktopSidebar={handleToggleDesktopSidebar}
      />
      <div className="ui-layout-wrapper block min-h-0 w-full flex-1 overflow-y-auto overflow-x-hidden px-[28px]"><Outlet /></div>
      <Toaster />
      <ModalRootElement />
    </div>
  )
}

export default AuthenticatedLayout
